import React, { useEffect, useMemo, useState } from 'react';
import { useSnackbar } from 'notistack';
import ActionSheet from './ActionSheet';
import {
  AdminTariff,
  ProtocolLimitPeriod,
  TariffPayload,
  createTariff,
  getTariffs,
  updateTariff,
} from '../../../utils/adminApiUtils';

interface TariffFormState {
  name: string;
  description: string;
  price_monthly: string;
  price_yearly_total: string;
  price_yearly_monthly: string;
  currency: string;
  protocol_limit: string;
  protocol_limit_period: ProtocolLimitPeriod;
  is_active: boolean;
}

const emptyForm: TariffFormState = {
  name: '',
  description: '',
  price_monthly: '',
  price_yearly_total: '',
  price_yearly_monthly: '',
  currency: 'RUB',
  protocol_limit: '',
  protocol_limit_period: 'monthly',
  is_active: true,
};

const TariffsManager: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [tariffs, setTariffs] = useState<AdminTariff[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [formState, setFormState] = useState<TariffFormState>(emptyForm);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [editingTariffId, setEditingTariffId] = useState<number | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);
  const [actionSheetOpen, setActionSheetOpen] = useState<number | null>(null);
  const [actionSheetPosition, setActionSheetPosition] = useState<{ x: number; y: number } | undefined>(undefined);

  const hasChanges = useMemo(() => {
    if (editingTariffId === null) {
      return JSON.stringify(formState) !== JSON.stringify(emptyForm);
    }

    const original = tariffs.find((tariff) => tariff.id === editingTariffId);
    if (!original) {
      return false;
    }

    return (
      formState.name !== original.name ||
      formState.description !== (original.description || '') ||
      formState.price_monthly !== original.price_monthly.toString() ||
      formState.price_yearly_total !== original.price_yearly_total.toString() ||
      formState.price_yearly_monthly !== original.price_yearly_monthly.toString() ||
      formState.currency !== original.currency ||
      formState.protocol_limit !== original.protocol_limit.toString() ||
      formState.protocol_limit_period !== original.protocol_limit_period ||
      formState.is_active !== original.is_active
    );
  }, [editingTariffId, formState, tariffs]);

  const loadTariffs = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getTariffs();
      setTariffs(data);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Ошибка загрузки тарифов';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadTariffs();
  }, []);

  const clearForm = () => {
    setFormState(emptyForm);
    setEditingTariffId(null);
  };

  const computeYearlyMonthly = (totalValue: string): string => {
    const parsed = parseFloat(totalValue);
    if (Number.isNaN(parsed) || parsed <= 0) {
      return '';
    }
    return (parsed / 12).toFixed(2);
  };

  const openCreateDialog = () => {
    clearForm();
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    clearForm();
  };

  const handleEdit = (tariff: AdminTariff) => {
    setEditingTariffId(tariff.id);
    setFormState({
      name: tariff.name,
      description: tariff.description || '',
      price_monthly: tariff.price_monthly.toString(),
      price_yearly_total: tariff.price_yearly_total.toString(),
      price_yearly_monthly: tariff.price_yearly_monthly.toString(),
      currency: tariff.currency,
      protocol_limit: tariff.protocol_limit.toString(),
      protocol_limit_period: tariff.protocol_limit_period,
      is_active: tariff.is_active,
    });
    setIsDialogOpen(true);
  };

  const handleChange = (field: keyof TariffFormState, value: string | boolean) => {
    setFormState((prev) => {
      if (field === 'price_yearly_total') {
        const stringValue = value as string;
        return {
          ...prev,
          price_yearly_total: stringValue,
          price_yearly_monthly: computeYearlyMonthly(stringValue),
        };
      }

      return {
        ...prev,
        [field]: value,
      };
    });
  };

  const buildPayload = (): TariffPayload => ({
    name: formState.name.trim(),
    description: formState.description.trim() || undefined,
    price_monthly: parseFloat(formState.price_monthly),
    price_yearly_total: parseFloat(formState.price_yearly_total),
    price_yearly_monthly: parseFloat(computeYearlyMonthly(formState.price_yearly_total)),
    currency: formState.currency.trim() || 'RUB',
    protocol_limit: parseInt(formState.protocol_limit, 10),
    protocol_limit_period: formState.protocol_limit_period,
    is_active: formState.is_active,
  });

  const validateForm = (): string | null => {
    if (!formState.name.trim()) {
      return 'Название тарифа обязательно';
    }

    if (!formState.price_monthly || !formState.price_yearly_total) {
      return 'Укажите все значения стоимости';
    }

    if (!formState.protocol_limit) {
      return 'Укажите лимит протоколов';
    }

    const priceMonthly = parseFloat(formState.price_monthly);
    const priceYearlyTotal = parseFloat(formState.price_yearly_total);
    const priceYearlyMonthly = parseFloat(formState.price_yearly_monthly);
    const protocolLimit = parseInt(formState.protocol_limit, 10);

    if ([priceMonthly, priceYearlyTotal, priceYearlyMonthly].some((v) => Number.isNaN(v) || v <= 0)) {
      return 'Стоимость должна быть положительным числом';
    }

    if (Number.isNaN(protocolLimit) || protocolLimit <= 0) {
      return 'Лимит протоколов должен быть положительным числом';
    }

    return null;
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const validationError = validateForm();
    if (validationError) {
      enqueueSnackbar(validationError, { variant: 'error' });
      return;
    }

    setSubmitting(true);

    try {
      const payload = buildPayload();

      if (editingTariffId === null) {
        await createTariff(payload);
        enqueueSnackbar('Тариф создан', { variant: 'success' });
      } else {
        await updateTariff(editingTariffId, payload);
        enqueueSnackbar('Тариф обновлен', { variant: 'success' });
      }

      await loadTariffs();
      setIsDialogOpen(false);
      clearForm();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Не удалось сохранить тариф';
      enqueueSnackbar(message, { variant: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  const formatCurrency = (value: number, currency: string) => {
    try {
      return value.toLocaleString('ru-RU', {
        style: 'currency',
        currency,
        minimumFractionDigits: 2,
      });
    } catch {
      return `${value.toFixed(2)} ${currency}`;
    }
  };

  return (
    <>
      <section className="tariffs-section">
        <div className="section-header">
          <h2>Управление тарифами</h2>
          <div className="actions">
            <button type="button" className="button-primary" onClick={openCreateDialog}>
              Создать тариф
            </button>
          </div>
        </div>

        {error && (
          <div className="error-banner">
            <span>{error}</span>
            <button type="button" onClick={() => void loadTariffs()}>
              Повторить
            </button>
          </div>
        )}

        {loading ? (
          <div className="loading small">Загрузка тарифов...</div>
        ) : tariffs.length === 0 ? (
          <div className="empty-state">
            <p>Тарифы не найдены. Создайте первый тариф с помощью кнопки «Создать тариф».</p>
          </div>
        ) : (
          <div className="users-table-container">
            <table className="admin-table users-table">
              <thead>
                <tr>
                  <th>Название</th>
                  <th>Стоимость / мес.</th>
                  <th>Стоимость / год</th>
                  <th>Лимит протоколов</th>
                  <th>Статус</th>
                </tr>
              </thead>
              <tbody>
                {tariffs.map((tariff) => (
                  <tr
                    key={tariff.id}
                    onClick={(e) => {
                      setActionSheetPosition({
                        x: e.clientX,
                        y: e.clientY,
                      });
                      setActionSheetOpen(tariff.id);
                    }}
                    style={{ cursor: 'pointer' }}
                  >
                    <td>
                      <div className="tariff-name">
                        <strong>{tariff.name}</strong>
                        {tariff.description && <span>{tariff.description}</span>}
                      </div>
                    </td>
                    <td>{formatCurrency(tariff.price_monthly, tariff.currency)}</td>
                    <td>
                      <div className="tariff-price">
                        <span>{formatCurrency(tariff.price_yearly_total, tariff.currency)}</span>
                        <small>
                          {formatCurrency(tariff.price_yearly_monthly, tariff.currency)} в мес.
                        </small>
                      </div>
                    </td>
                    <td>
                      <div className="tariff-limit">
                        <strong>{tariff.protocol_limit}</strong>
                        <span>
                          / {tariff.protocol_limit_period === 'monthly' ? 'месяц' : 'год'}
                        </span>
                      </div>
                    </td>
                    <td>
                      <span className={`status-dot ${tariff.is_active ? 'active' : 'inactive'}`}>
                        {tariff.is_active ? 'Активен' : 'Неактивен'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {actionSheetOpen !== null && (
        <ActionSheet
          isOpen={true}
          position={actionSheetPosition}
          onClose={() => {
            setActionSheetOpen(null);
            setActionSheetPosition(undefined);
          }}
          options={[
            {
              label: 'Редактировать',
              action: () => {
                const tariff = tariffs.find(t => t.id === actionSheetOpen);
                if (tariff) handleEdit(tariff);
              },
            },
          ]}
        />
      )}

      <TariffDialog
        isOpen={isDialogOpen}
        mode={editingTariffId === null ? 'create' : 'edit'}
        formState={formState}
        onChange={handleChange}
        onClose={handleCloseDialog}
        onSubmit={handleSubmit}
        submitting={submitting}
        canSubmit={hasChanges}
      />
    </>
  );
};

interface TariffDialogProps {
  isOpen: boolean;
  mode: 'create' | 'edit';
  formState: TariffFormState;
  onChange: (field: keyof TariffFormState, value: string | boolean) => void;
  onClose: () => void;
  onSubmit: (event: React.FormEvent<HTMLFormElement>) => void;
  submitting: boolean;
  canSubmit: boolean;
}

const TariffDialog: React.FC<TariffDialogProps> = ({
  isOpen,
  mode,
  formState,
  onChange,
  onClose,
  onSubmit,
  submitting,
  canSubmit,
}) => {
  if (!isOpen) {
    return null;
  }

  return (
    <div className="dialog-overlay" onClick={onClose}>
      <div className="tariff-dialog" onClick={(event) => event.stopPropagation()}>
        <div className="dialog-header">
          <h3>{mode === 'create' ? 'Создать тариф' : 'Редактировать тариф'}</h3>
          <button type="button" className="dialog-close" onClick={onClose}>
            ×
          </button>
        </div>

        <form className="tariff-form" onSubmit={onSubmit}>
          <div className="form-row">
            <label>
              Название *
              <input
                type="text"
                value={formState.name}
                onChange={(event) => onChange('name', event.target.value)}
                placeholder="Введите название тарифа"
                required
              />
            </label>
          </div>

          <div className="form-row">
            <label>
              Описание
              <textarea
                value={formState.description}
                onChange={(event) => onChange('description', event.target.value)}
                placeholder="Краткое описание тарифа"
                rows={3}
              />
            </label>
          </div>

          <div className="form-row grid-two">
            <label>
              Стоимость (месяц) *
              <input
                type="number"
                min="0"
                step="0.01"
                value={formState.price_monthly}
                onChange={(event) => onChange('price_monthly', event.target.value)}
                placeholder="1000.00"
                required
              />
            </label>
            <label>
              Стоимость (год) *
              <input
                type="number"
                min="0"
                step="0.01"
                value={formState.price_yearly_total}
                onChange={(event) => onChange('price_yearly_total', event.target.value)}
                placeholder="10000.00"
                required
              />
            </label>
          </div>

          <div className="form-row grid-two">
            <label>
              Стоимость месяца при оплате за год
              <input
                type="text"
                value={formState.price_yearly_monthly}
                readOnly
                placeholder="Автосчёт"
              />
            </label>
            <label>
              Валюта
              <input
                type="text"
                value={formState.currency}
                onChange={(event) => onChange('currency', event.target.value.toUpperCase())}
                maxLength={3}
                placeholder="RUB"
              />
            </label>
          </div>

          <div className="form-row grid-two">
            <label>
              Лимит протоколов *
              <input
                type="number"
                min="1"
                step="1"
                value={formState.protocol_limit}
                onChange={(event) => onChange('protocol_limit', event.target.value)}
                placeholder="50"
                required
              />
            </label>
            <label>
              Период лимита *
              <select
                value={formState.protocol_limit_period}
                onChange={(event) =>
                  onChange('protocol_limit_period', event.target.value as ProtocolLimitPeriod)
                }
              >
                <option value="monthly">Ежемесячно</option>
                <option value="yearly">Ежегодно</option>
              </select>
            </label>
          </div>

          <div className="form-row checkbox-row">
            <label className="checkbox">
              <input
                type="checkbox"
                checked={formState.is_active}
                onChange={(event) => onChange('is_active', event.target.checked)}
              />
              <span>Активный тариф</span>
            </label>
          </div>

          <div className="dialog-actions">
            <button type="button" className="secondary" onClick={onClose}>
              Отмена
            </button>
            <button type="submit" className="primary" disabled={submitting || !canSubmit}>
              {submitting
                ? mode === 'create'
                  ? 'Создание...'
                  : 'Сохранение...'
                : mode === 'create'
                ? 'Создать тариф'
                : 'Сохранить изменения'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TariffsManager;

